import { useEffect, useRef, useCallback } from 'react';
import { useGameStore } from '@hollowveil/shared';
import './ZoomControls.css';

export function ZoomControls() {
  const { cameraZoom, setZoom, zoomIn, zoomOut, showZoomControls } = useGameStore();
  const lastPinchDistance = useRef<number | null>(null);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    // Prevent page scroll
    e.preventDefault();

    const delta = e.deltaY > 0 ? -5 : 5;
    setZoom(cameraZoom + delta);
  }, [cameraZoom, setZoom]);

  // Handle pinch zoom for mobile
  const handleTouchStart = useCallback((e: TouchEvent) => {
    // Track active touch count globally for GameScene to check
    (window as unknown as { __activeTouches?: number }).__activeTouches = e.touches.length;

    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      lastPinchDistance.current = distance;
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    // Update active touch count
    (window as unknown as { __activeTouches?: number }).__activeTouches = e.touches.length;

    if (e.touches.length === 2 && lastPinchDistance.current !== null) {
      e.preventDefault();

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      const delta = (distance - lastPinchDistance.current) * 0.2;
      setZoom(cameraZoom + delta);
      lastPinchDistance.current = distance;
    }
  }, [cameraZoom, setZoom]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    // Update active touch count (remaining touches after this one ends)
    (window as unknown as { __activeTouches?: number }).__activeTouches = e.touches.length;
    lastPinchDistance.current = null;
  }, []);

  // Attach event listeners
  useEffect(() => {
    const canvas = document.querySelector('canvas');
    if (!canvas) return;

    // Mouse wheel - passive: false to allow preventDefault
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    // Touch events for pinch zoom
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Calculate zoom percentage for display
  const zoomPercent = Math.round((cameraZoom / 50) * 100);

  // Don't render buttons if hidden, but keep event listeners active
  if (!showZoomControls) {
    return null;
  }

  return (
    <div className="zoom-controls">
      <button className="zoom-btn" onClick={zoomOut} title="Zoom arrière">
        −
      </button>
      <span className="zoom-level">{zoomPercent}%</span>
      <button className="zoom-btn" onClick={zoomIn} title="Zoom avant">
        +
      </button>
    </div>
  );
}
